import { getRouteApi } from "@tanstack/react-router";
import debounce from "lodash/debounce";
import React from "react";

import { SearchField, SearchInput } from "@/components/ui/search-field";

const routeApi = getRouteApi("/_auth/_app/runs/");

export function TestRunSearch() {
  const { inputValue, onChange, onSubmit, onClear } = useDebouncedSearch();

  return (
    <SearchField
      aria-label="Search runs"
      value={inputValue}
      onChange={onChange}
      onSubmit={onSubmit}
      onClear={onClear}
    >
      <SearchInput placeholder="Search runs" />
    </SearchField>
  );
}

function useDebouncedSearch(delay = 500) {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  // Local state for the input value
  const [inputValue, setInputValue] = React.useState(search.q ?? "");

  // Sync local state when URL search param changes externally
  React.useEffect(() => {
    setInputValue(search.q ?? "");
  }, [search.q]);

  const onSubmit = React.useCallback(
    (q: string | undefined) => {
      navigate({ search: { q }, replace: true });
    },
    [navigate],
  );

  // Create debounced function once and reuse it
  const debouncedSubmit = React.useMemo(
    () => debounce(onSubmit, delay),
    [onSubmit, delay],
  );

  // Clean up the debounced function on unmount
  React.useEffect(() => {
    return () => {
      debouncedSubmit.cancel();
    };
  }, [debouncedSubmit]);

  const onChange = React.useCallback(
    (q: string | undefined) => {
      // Update local state immediately for responsive UI
      setInputValue(q ?? "");
      // Debounce the URL update
      debouncedSubmit(q);
    },
    [debouncedSubmit],
  );

  const onClear = React.useCallback(() => {
    setInputValue("");
    debouncedSubmit.cancel();
    navigate({ search: { q: undefined } });
  }, [debouncedSubmit, navigate]);

  const handleSubmit = React.useCallback(() => {
    onSubmit(inputValue);
  }, [onSubmit, inputValue]);

  return {
    inputValue,
    onChange,
    onSubmit: handleSubmit,
    onClear,
  };
}
